import { useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { SecureImage } from "@/components/SecureImage";

export function PhotoApprovals() {
  const [items, setItems] = useState<any[]>([]);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/photos").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const decide = async (id: string, decision: "approved" | "rejected") => {
    setBusy(id);
    try { await api.post(`/admin/photos/${id}`, { decision }); toast.success(decision === "approved" ? "Photo approved — now visible to families" : "Photo rejected — caregiver asked to resend"); load(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(""); }
  };
  if (items.length === 0) return null;
  return (
    <section className="mb-8 rounded-2xl border border-amber-200 bg-amber-50/60 p-5" data-testid="photo-approvals">
      <h2 className="font-heading text-lg font-semibold text-brand-navy">Profile photos awaiting approval <span className="ml-2 rounded-full bg-amber-200 px-2 py-0.5 text-xs font-bold text-amber-900" data-testid="photo-approvals-count">{items.length}</span></h2>
      <p className="mt-1 text-xs text-brand-slate">Families only see a photo once you approve it. Check the face is clear and matches the ID selfie.</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((c) => (
          <article key={c.caregiver_id} className="rounded-xl bg-white p-3 shadow-subtle" data-testid="photo-approval-card">
            <div className="flex gap-3">
              <SecureImage fileId={c.pending_photo_file_id} alt={`${c.name} new photo`} className="h-24 w-24 rounded-xl object-cover" />
              {c.photo_file_id && <div className="text-center"><SecureImage fileId={c.photo_file_id} alt="current" className="h-16 w-16 rounded-lg object-cover opacity-70" /><p className="mt-1 text-[10px] text-brand-slate">current</p></div>}
            </div>
            <p className="mt-3 font-semibold text-brand-navy" data-testid="photo-approval-name">{c.name}</p>
            <p className="text-xs text-brand-slate">{c.city} · {fmtDate(c.pending_photo_at)}</p>
            <div className="mt-3 flex gap-2">
              <button disabled={busy === c.caregiver_id} onClick={() => decide(c.caregiver_id, "approved")} className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg bg-brand-emerald py-1.5 text-xs font-semibold text-white hover:bg-brand-emeraldHover disabled:opacity-50" data-testid="approve-photo-button"><Check className="h-3.5 w-3.5" />Approve</button>
              <button disabled={busy === c.caregiver_id} onClick={() => decide(c.caregiver_id, "rejected")} className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg border border-red-200 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50" data-testid="reject-photo-button"><X className="h-3.5 w-3.5" />Reject</button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
